import { createReducer, createAction } from '@reduxjs/toolkit';
import i18n from 'i18n-js';

import { backendCall, createApiCall, eventCall, unlogin } from '@reducers/utils';
import { Alert, getSecureValue } from '@helpers';
import { updateCompanyProfile } from '@reducers/user';
import { goToOffline, goToResetting, saveOldStatuses, restoreStatuses, stopConnectorInfo } from '@reducers/connector';
import { resetOnboardingData } from '@reducers/onBoarding';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import type { RootState, AppThunk } from '@store/types';
import type {
  MembershipState,
  UserItem,
  SearchUsersResponse,
  DeleteUserResponse,
  ConnectorIpRange,
  ConnectorIpScanStatus,
  ConnectorProductionConfigurationDetailsResponse,
  Timezone,
  Country,
  ApiKey,
  ImnooApiKey,
  UpdateImnooApiKeyResponse,
  GenerateApiKeyResponse,
  ChangeAccessResponse,
  SearchUsersRequestDto,
  DeleteUserRequestDto,
  InviteUserRequestDto,
  UpdateUserProfileRequestDto,
  RemoveTenantRequestDto,
  ChangeAccessRequestDto,
  ConfigureIpRangeForScanRequestDto,
  RemoveIpRangeFromScanRequestDto,
  RequestForIpRangeScanRequestDto,
  UpdateImnooApiKeyRequestDto,
  GenerateApiKeyRequestDto,
  ConfigureNetworkScanRequestDto,
  RemoveTenantResponse,
  GetUsersProps,
  SearchUsersProps,
  SearchInvitedUsersProps,
} from './types';

// Re-export types for consumers
export type {
  MembershipState,
  UserItem,
  SearchUsersResponse,
  DeleteUserResponse,
  ConnectorIpRange,
  ConnectorIpScanStatus,
  ConnectorProductionConfigurationDetailsResponse,
} from './types';

// === Initial State ===

const initState: MembershipState = {
  users: [],
  isUsersMore: true,
  loaded: false,
  pageNumber: 1,
  pageSize: 20,
  invitedUsers: [],
  isInvitedUsersMore: true,
  invitedLoaded: false,
  invitedPageNumber: 1,
  isCompanyRemoving: false,
  connectorIpRange: [],
  ipScanUpdatedOn: null,
  ipScanStatus: null,
  isWithERP: null,
  connectorDetails: null,
  connectorDetailsLoaded: false,
};

// === Selectors ===

export const selectIsWithERP = (state: RootState) => state.membership.isWithERP;
export const selectConnectorDetails = (state: RootState) => state.membership.connectorDetails;

// === Actions ===

export const clear = createAction('clear');
export const close = createAction('membership/close');
export const setUsers = createAction<Partial<MembershipState>>('membership/setUsers');
export const deleteUserFromStore = createAction<UserItem[]>('membership/deleteUserFromStore');
export const updatePageNumber = createAction('membership/updatePageNumber');
export const resetPageNumber = createAction('membership/resetPageNumber');

export const setInvitedUsers = createAction<Partial<MembershipState>>('membership/setInvitedUsers');
export const deleteInvitedUserFromStore = createAction<UserItem[]>('membership/deleteInvitedUserFromStore');
export const updateInvitedPageNumber = createAction('membership/updateInvitedPageNumber');
export const resetInvitedPageNumber = createAction('membership/resetInvitedPageNumber');
export const updateIsCompanyRemoving = createAction<boolean>('membership/updateIsCompanyRemoving');
export const setConnectorIpRange = createAction<ConnectorIpRange[]>('membership/connectorIpRange');
export const setSearchStatus = createAction<ConnectorIpScanStatus>('membership/searchStatus');
export const setIsWithERP = createAction<boolean>('membership/isWithERP');
export const setConnectorDetails = createAction<MembershipState['connectorDetails']>('membership/connectorDetails');

// === Thunks ===

export const getUsers =
  (props: GetUsersProps = {}): AppThunk<Promise<SearchUsersResponse | undefined>> =>
  async (dispatch) => {
    const { pageNumber, pageSize, orderBy, filters } = props;
    return await dispatch(
      backendCall(
        createApiCall((api) => api.membership.searchUsers, {
          params: { pageNumber, pageSize, orderBy, filters },
        })
      )
    );
  };

export const searchUsers =
  (props: SearchUsersProps = {}): AppThunk<Promise<UserItem[]>> =>
  async (dispatch, getState) => {
    const { reset, orderBy, size, callback } = props;

    if (reset) {
      dispatch(resetPageNumber());
    } else {
      dispatch(updatePageNumber());
    }

    const {
      membership: { pageNumber, pageSize, users = [] },
    } = getState();

    const amount = size || pageSize;

    let fetched: UserItem[] = [];

    await dispatch(
      backendCall(
        createApiCall((api) => api.membership.searchUsers, {
          params: {
            pageNumber,
            pageSize: amount,
            orderBy,
            filters: {
              status: {
                value: 'Registered',
                valueType: 'string',
              },
            },
          } as SearchUsersRequestDto,
          onSuccess: (data: SearchUsersResponse) => {
            const nextUsers = data.users || [];
            const isUsersMore = nextUsers.length === amount;
            if (reset) {
              fetched = nextUsers;
              dispatch(setUsers({ isUsersMore, users: nextUsers }));
            } else {
              fetched = [...users, ...nextUsers];
              dispatch(setUsers({ isUsersMore, users: fetched }));
            }
            if (callback) {
              callback();
            }
          },
          onError: (error) => {
            console.log(error, 'error');
          },
        })
      )
    );

    return fetched;
  };

export const searchInvitedUsers =
  (props: SearchInvitedUsersProps = {}): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const { reset, orderBy, size, callback } = props;

    if (reset) {
      dispatch(resetInvitedPageNumber());
    } else {
      dispatch(updateInvitedPageNumber());
    }

    const {
      membership: { invitedPageNumber, pageSize, invitedUsers = [] },
    } = getState();

    const amount = size || pageSize;

    await dispatch(
      backendCall(
        createApiCall((api) => api.membership.searchUsers, {
          params: {
            pageNumber: invitedPageNumber,
            pageSize: amount,
            filters: {
              status: {
                value: 'Invited',
                valueType: 'string',
              },
            },
            orderBy:
              orderBy ||
              ({
                invitedOn: 'Desc',
              } as SearchUsersRequestDto['orderBy']),
          } as SearchUsersRequestDto,
          onSuccess: (data: SearchUsersResponse) => {
            const isInvitedUsersMore = (data.users || []).length === amount;
            if (reset) {
              dispatch(setInvitedUsers({ isInvitedUsersMore, invitedUsers: data.users || [] }));
            } else {
              dispatch(setInvitedUsers({ isInvitedUsersMore, invitedUsers: [...invitedUsers, ...(data.users || [])] }));
            }
            if (callback) {
              callback();
            }
          },
          onError: (error) => {
            console.log(error, 'error');
          },
        })
      )
    );
  };

export const getById =
  (id: string): AppThunk<Promise<SearchUsersResponse | undefined>> =>
  async (dispatch) => {
    return await dispatch(
      backendCall(
        createApiCall((api) => api.membership.searchUsers, {
          params: {
            pageNumber: 1,
            pageSize: 1,
            filters: {
              UserId: {
                value: id,
                valueType: 'string',
              },
            },
            orderBy: {},
          } as SearchUsersRequestDto,
        })
      )
    );
  };

export const inviteUser =
  (data: InviteUserRequestDto, onClose?: () => void): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const {
      membership: { invitedUsers = [] },
    } = getState();

    await dispatch(
      backendCall(
        createApiCall((api) => api.membership.inviteUser, {
          onSuccess: () => {
            dispatch(
              setInvitedUsers({
                invitedUsers: [
                  {
                    ...data,
                    invitedOn: new Date().toISOString(),
                  } as UserItem,
                  ...invitedUsers,
                ],
              })
            );
            trackEvent(TRACKING_EVENTS.USER_MANAGEMENT, {
              action: 'added',
              role: ((data as Record<string, unknown>)?.role as string) || '',
              company: ((data as Record<string, unknown>)?.company as string) || '',
              invitedByEmail: ((data as Record<string, unknown>)?.invitedByEmail as string) || '',
            });
            if (onClose) {
              onClose();
            }
          },
          params: data,
          onError: () => {},
        })
      )
    );
  };

export const getConnectorIpRange = (): AppThunk<Promise<void>> => async (dispatch) => {
  await dispatch(
    backendCall(
      createApiCall((api) => api.membership.connectorIpRange, {
        onSuccess: (data: ConnectorIpRange[]) => {
          dispatch(setConnectorIpRange(data));
        },
        onError: (error) => {
          console.log(error, 'error');
        },
      })
    )
  );
};

export const getTimezone = (): AppThunk<Promise<Timezone[] | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.membership.getTimezone, {
        anonym: true,
      })
    )
  );
};

export const connectorProductionConfigurationDetails =
  (): AppThunk<Promise<ConnectorProductionConfigurationDetailsResponse | undefined>> => async (dispatch) => {
    return await dispatch(
      backendCall(
        createApiCall((api) => api.membership.connectorProductionConfigurationDetails, {
          onSuccess: (data: ConnectorProductionConfigurationDetailsResponse) => {
            if (data?.sourceSystems?.length) {
              dispatch(setConnectorDetails(data.sourceSystems[0]));
            } else {
              dispatch(setConnectorDetails(null));
            }
          },
        })
      )
    );
  };

export const getCountryCodes = (): AppThunk<Promise<Country[] | undefined>> => async (dispatch) => {
  return await dispatch(backendCall(createApiCall((api) => api.membership.getCountryCodes, {})));
};

export const connectorIpScanStatus = (): AppThunk<Promise<void>> => async (dispatch) => {
  await dispatch(
    backendCall(
      createApiCall((api) => api.membership.connectorIpScanStatus, {
        onSuccess: (data: ConnectorIpScanStatus[]) => {
          if (data?.[0]) {
            dispatch(setSearchStatus(data[0]));
          }
        },
      })
    )
  );
};

export const removeIpRangeFromScan =
  (data: RemoveIpRangeFromScanRequestDto): AppThunk<Promise<void>> =>
  async (dispatch) => {
    const call = createApiCall((api) => api.membership.removeIpRangeFromScan, {
      params: data,
    });
    await dispatch(backendCall(call));

    await dispatch(
      eventCall({
        connectionName: 'appHub',
        eventName: 'OnRemoveIpRangeFromScanRequest',
        data: call,
        timeout: 120,
      })
    );
  };

export const requestIpRangeScan =
  (data: RequestForIpRangeScanRequestDto): AppThunk<Promise<void>> =>
  async (dispatch) => {
    await dispatch(
      backendCall(
        createApiCall((api) => api.membership.requestIpRangeScan, {
          onSuccess: (data) => {
            console.log(data, 'data');
          },
          params: data,
          onError: (error) => {
            console.log(error, 'error');
          },
        })
      )
    );
  };

export const configureIpRangeForScan =
  (data: ConfigureIpRangeForScanRequestDto): AppThunk<Promise<void>> =>
  async (dispatch) => {
    const call = createApiCall((api) => api.membership.configureIpRangeForScan, {
      params: data,
    });

    await dispatch(
      eventCall({
        connectionName: 'appHub',
        eventName: 'OnConfigureIpRangeForScanRequest',
        data: call,
        timeout: 120,
      })
    );
  };

export const updateUserProfile =
  (data: UpdateUserProfileRequestDto, onClose?: () => void): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const {
      membership: { users, invitedUsers },
    } = getState();

    await dispatch(
      backendCall(
        createApiCall((api) => api.membership.updateUserProfile, {
          onSuccess: () => {
            if ((data as Record<string, unknown>).role) {
              const previousUser =
                users.find((u) => u.emailAddress === data.emailAddress) || invitedUsers.find((u) => u.emailAddress === data.emailAddress);
              if (previousUser && previousUser.role !== (data as Record<string, unknown>).role) {
                trackEvent(TRACKING_EVENTS.USER_MANAGEMENT, {
                  action: 'roleChanged',
                  role: (data as Record<string, unknown>).role as string,
                  previousRole: previousUser.role || '',
                });
              }
            }

            if (onClose) {
              onClose();
            }

            const newUsers = users.map((u) => {
              if (u.emailAddress === data.emailAddress) {
                return {
                  ...u,
                  ...data,
                };
              }
              return u;
            });
            const newInvitedUsers = invitedUsers.map((u) => {
              if (u.emailAddress === data.emailAddress) {
                return {
                  ...u,
                  ...data,
                };
              }
              return u;
            });
            dispatch(setUsers({ users: newUsers }));
            dispatch(setInvitedUsers({ invitedUsers: newInvitedUsers }));
          },
          params: data,
          onError: () => {},
        })
      )
    );
  };

export const updateCompanyProfileValue =
  (obj: Record<string, unknown>): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const {
      user: { userInfo = {} },
    } = getState() as RootState & { user: { userInfo: Record<string, unknown> } };

    const data = {
      companyProfile: {
        ...(userInfo?.companyProfile as Record<string, unknown>),
        ...obj,
      },
      emailAddress: userInfo.emailAddress,
      userProfile: userInfo.userProfile,
    };

    await dispatch(
      backendCall(
        createApiCall((api) => api.membership.updateUserProfile, {
          onSuccess: () => {
            dispatch(updateCompanyProfile(obj));
          },
          params: data as UpdateUserProfileRequestDto,
        })
      )
    );
  };

export const deleteUser =
  (email: string): AppThunk<Promise<DeleteUserResponse | undefined>> =>
  async (dispatch, getState) => {
    const {
      membership: { users, invitedUsers },
    } = getState();

    return await dispatch(
      backendCall(
        createApiCall((api) => api.membership.deleteUser, {
          onSuccess: (r: DeleteUserResponse) => {
            if (!r.isSuccess) {
              Alert({ title: r.errorMessage as string });
              return;
            }

            trackEvent(TRACKING_EVENTS.USER_MANAGEMENT, { action: 'deleted' });

            const newUsers = users.filter((u) => u.emailAddress !== email);
            const newInvitedUsers = invitedUsers.filter((u) => u.emailAddress !== email);
            dispatch(deleteUserFromStore(newUsers));
            dispatch(deleteInvitedUserFromStore(newInvitedUsers));
          },
          params: { emailAddress: email } as DeleteUserRequestDto,
          onError: () => {},
        })
      )
    );
  };

export const deviceAliveEvent = (): AppThunk<Promise<void>> => async (dispatch) => {
  await dispatch(
    backendCall(
      createApiCall((api) => api.membership.deviceAliveEvent, {
        onSuccess: () => {},
        onError: (error) => {
          console.log(error, 'error');
        },
        ignoreDefaultErrorHandler: true,
      })
    )
  );
};

export const removeTenant =
  (authorizationCode?: string): AppThunk<Promise<RemoveTenantResponse | undefined>> =>
  async (dispatch) => {
    const tenantId = getSecureValue('tid');

    const data: Record<string, unknown> = {
      tenantId: Number(tenantId) || null,
    };

    if (authorizationCode) {
      data.authorizationCode = authorizationCode;
    }

    return await dispatch(
      backendCall(
        createApiCall((api) => api.membership.removeTenant, {
          params: data as RemoveTenantRequestDto,
        })
      )
    );
  };

export const resetConnector =
  (setResetLoading: (loading: boolean) => void): AppThunk<Promise<void>> =>
  async (dispatch) => {
    setResetLoading(true);
    stopConnectorInfo();
    const call = createApiCall((api) => api.membership.resetConnector, {});

    try {
      dispatch(saveOldStatuses());
      dispatch(goToResetting());
      dispatch(resetOnboardingData());
      await dispatch(
        eventCall({
          connectionName: 'appHub',
          eventName: 'OnResetedConnector',
          eventNameError: 'OnFailedToResetConnector',
          data: call,
          timeout: 120,
        })
      );

      dispatch(goToOffline());
      Alert({ title: 'Success', type: 'success' });
    } catch (e) {
      dispatch(restoreStatuses());
      console.log(e, 'e');
      Alert({ title: 'Error', type: 'danger' });
    } finally {
      setResetLoading(false);
    }
  };

export const configureNetworkScan =
  (value: boolean): AppThunk<Promise<unknown>> =>
  async (dispatch) => {
    const call = createApiCall((api) => api.membership.configureNetworkScan, {
      params: {
        isEnabled: value,
      } as ConfigureNetworkScanRequestDto,
    });

    const result = await dispatch(
      eventCall({
        connectionName: 'appHub',
        eventName: 'OnChangedWorkerConfiguration',
        data: call,
        timeout: 120,
      })
    );

    await dispatch(connectorIpScanStatus());

    return result;
  };

export const onFinishedRemoveTenant =
  (e: Record<string, unknown>): AppThunk<Promise<void>> =>
  async (dispatch) => {
    if (e?.Status === 'SuccessfullyRemoved') {
      dispatch(unlogin());
    } else {
      Alert({ title: i18n.t('Error'), type: 'danger' });
    }
  };

export const onLoginAccountCreated =
  (e: unknown): AppThunk<Promise<void>> =>
  async (dispatch) => {
    Alert({ title: i18n.t('Invitation Sent'), type: 'success' });
    dispatch(searchInvitedUsers({ reset: true }));
  };

export const onLoginAccountCreationFailed =
  (e: { errorCode: number }): AppThunk<Promise<void>> =>
  async () => {
    const error = (title: string, text?: string) =>
      Alert({
        title: i18n.t(title),
        text: text ? i18n.t(text) : null,
        actions: [
          {
            text: 'Ok',
            onPress: () => console.log('Ok Pressed'),
            style: 'primary',
          },
        ],
      });

    switch (e.errorCode) {
      case 0:
        return error('Error', 'Please, check the data and try again');
      case 1:
        return error('User already exists in your company');
      case 2:
        return error('User already exists in another company', 'Use another email address');
      default:
        return Alert({ title: i18n.t('Error'), type: 'danger' });
    }
  };

export const changeAccess =
  (data: ChangeAccessRequestDto): AppThunk<Promise<ChangeAccessResponse | undefined>> =>
  async (dispatch) => {
    return await dispatch(
      backendCall(
        createApiCall((api) => api.membership.changeAccess, {
          params: data,
          onSuccess: (r) => {
            console.log(r, 'Success');
          },
          onError: (error) => {
            console.log(error, 'error');
          },
        })
      )
    );
  };

export const getApiKeys = (): AppThunk<Promise<ApiKey[] | undefined>> => async (dispatch) => {
  return await dispatch(backendCall(createApiCall((api) => api.membership.getApiKeys, {})));
};

export const getImnooApiKey = (): AppThunk<Promise<ImnooApiKey[] | undefined>> => async (dispatch) => {
  return await dispatch(backendCall(createApiCall((api) => api.membership.getImnooApiKey, {})));
};

export const updateImnooApiKey =
  (data: UpdateImnooApiKeyRequestDto): AppThunk<Promise<UpdateImnooApiKeyResponse | undefined>> =>
  async (dispatch) => {
    const response = await dispatch(
      backendCall(
        createApiCall((api) => api.membership.updateImnooApiKey, {
          params: data,
        })
      )
    );

    if (response && !response.isError) {
      Alert({ title: 'Success' });
    } else {
      Alert({ title: response?.errorMessage || 'Error' });
    }

    return response;
  };

export const generateApiKeys =
  (data: GenerateApiKeyRequestDto): AppThunk<Promise<GenerateApiKeyResponse | undefined>> =>
  async (dispatch) => {
    return await dispatch(
      backendCall(
        createApiCall((api) => api.membership.generateApiKeys, {
          params: data,
        })
      )
    );
  };

// === Reducer ===

export default createReducer<MembershipState>(initState, (builder) => {
  builder
    .addCase(clear, () => {
      return initState;
    })
    .addCase(close, () => {
      return initState;
    })
    .addCase(setUsers, (state, action) => ({
      ...state,
      loaded: true,
      ...action.payload,
    }))
    .addCase(setSearchStatus, (state, action) => ({
      ...state,
      ...action.payload,
    }))
    .addCase(setIsWithERP, (state, action) => ({
      ...state,
      isWithERP: action.payload,
    }))
    .addCase(setConnectorDetails, (state, action) => ({
      ...state,
      connectorDetails: action.payload,
      connectorDetailsLoaded: true,
    }))
    .addCase(setInvitedUsers, (state, action) => ({
      ...state,
      invitedLoaded: true,
      ...action.payload,
    }))
    .addCase(updateInvitedPageNumber, (state) => ({
      ...state,
      invitedPageNumber: state.invitedPageNumber + 1,
    }))
    .addCase(resetInvitedPageNumber, (state) => ({
      ...state,
      invitedPageNumber: 1,
    }))
    .addCase(updatePageNumber, (state) => ({
      ...state,
      pageNumber: state.pageNumber + 1,
    }))
    .addCase(resetPageNumber, (state) => ({
      ...state,
      pageNumber: 1,
    }))
    .addCase(deleteUserFromStore, (state, action) => ({
      ...state,
      users: action.payload,
    }))
    .addCase(updateIsCompanyRemoving, (state, action) => ({
      ...state,
      isCompanyRemoving: action.payload,
    }))
    .addCase(deleteInvitedUserFromStore, (state, action) => ({
      ...state,
      invitedUsers: action.payload,
    }))
    .addCase(setConnectorIpRange, (state, action) => ({
      ...state,
      connectorIpRange: action.payload,
    }));
});
